import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useAuthContext } from '@coffeeplexity/shared';
import { AppHeader } from '../components/ui/AppHeader';
import { AuthNavigator } from './AuthNavigator';
import { MainNavigator } from './MainNavigator';
import { LoadingScreen } from '../screens/LoadingScreen';
import { AuthGuard } from '../components/auth/AuthGuard';
import type { RootStackParamList } from './types';
import { defaultScreenOptions } from '../config/navigationConfig';

const Stack = createNativeStackNavigator<RootStackParamList>();

/**
 * RootNavigator - Production-Ready Root Navigation
 *
 * Features:
 * - Flattened URL structure for clean web URLs
 * - Optimized authentication flow
 * - Proper TypeScript integration
 * - Performance optimizations with lazy loading
 * - SEO-friendly routing configuration
 */
export function RootNavigator() {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthGuard>
      <Stack.Navigator
        initialRouteName={isAuthenticated ? 'main' : 'auth'}
        screenOptions={{
          ...defaultScreenOptions.stack,
          headerShown: false,
          header: props => <AppHeader {...props} />,
          // Performance optimization: enable native animations
          animationTypeForReplace: 'push',
          gestureEnabled: true,
        }}
      >
        {isAuthenticated ? (
          <Stack.Screen
            name='main'
            component={MainNavigator}
            options={{
              // Remove any nesting indicators from URL
              headerShown: false,
              gestureEnabled: false, // Disable gesture on main screen
            }}
          />
        ) : (
          <Stack.Screen
            name='auth'
            component={AuthNavigator}
            options={{
              headerShown: false,
              gestureEnabled: false, // Disable gesture on auth screen
            }}
          />
        )}
      </Stack.Navigator>
    </AuthGuard>
  );
}
